import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart } from '@fortawesome/pro-solid-svg-icons';
import Drawer from '../../Drawer/Drawer';
import { useInfiniteStoryLikers } from '../../../../queries/ephemeralStories';
import StoryViewerListItem from './StoryViewerListItem';
import './StoryViewersModal.scss';

// Modale enregistrée dans modalTypes (cf. src/index.js). Reçoit `storyId`, `handleClose`,
// `totalLikes` (count.totalReactions de la story) du ModalManager. Mirror de StoryViewersModal côté UX,
// adapté à l'endpoint /ephemeral-stories/:id/reactions (chaque item expose le réacteur via `owner`).
const StoryLikersModal = ({ storyId, handleClose, totalLikes = 0 }) => {
    const sentinelRef = useRef(null);

    const {
        data,
        isLoading,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = useInfiniteStoryLikers(storyId);

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasNextPage) return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting && !isFetchingNextPage) {
                fetchNextPage();
            }
        }, { rootMargin: '120px' });
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const likers = (data?.pages || []).flatMap((p) => p?.data || []);
    const totalFromApi = data?.pages?.[0]?.total || 0;
    // Garde le total optimistic du caller si l'API n'a pas encore renvoyé sa pagination.
    const grandTotal = Math.max(totalLikes || 0, totalFromApi);

    return (
        <Drawer open={true} small handleClose={handleClose} headContent={
            <div className="story-viewers-modal-header">
                <div className="story-viewers-modal-header-main">
                    <FontAwesomeIcon icon={faHeart} className="story-viewers-modal-header-icon"/>
                    <span className="story-viewers-modal-header-count">{grandTotal}</span>
                    <span className="story-viewers-modal-header-label">
                        Like(s)
                    </span>
                </div>
            </div>
        }>
            <div className="story-viewers-modal">
                {isLoading && (
                    <div className="story-viewers-modal-state">Chargement…</div>
                )}

                {isError && !isLoading && (
                    <div className="story-viewers-modal-state is-error">
                        Impossible de charger les likes pour le moment.
                    </div>
                )}

                {!isLoading && !isError && likers.length === 0 && (
                    <div className="story-viewers-modal-state">
                        Personne n'a encore liké cette story.
                    </div>
                )}

                {likers.length > 0 && (
                    <ul className="story-viewers-modal-list">
                        {likers.map((like) => (
                            <StoryViewerListItem
                                key={like._id || like.owner?._id}
                                viewer={like.owner}
                                createdAt={like.createdAt}
                                onClose={handleClose}
                            />
                        ))}
                        <li ref={sentinelRef} className="story-viewers-modal-sentinel" aria-hidden="true"/>
                        {isFetchingNextPage && (
                            <li className="story-viewers-modal-state">Chargement…</li>
                        )}
                    </ul>
                )}
            </div>
        </Drawer>
    );
};

export default StoryLikersModal;
